import {
	AuthenticationUser,
	AuthRoleEnum,
	useGetVetterJobQuery,
	useRefreshTokenMutation,
} from '@codezo/graphql';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { formatLoginUser } from '../../lib/format-auth-user';
import { clearJobLinkToken, getJobLinkToken } from '../../lib/job-session';
import { classifyLinkFailure } from '../../lib/link-failure';
import { JobAuthChallenge } from './JobAuthChallenge';
import { JobDetail, LinkJobHeader } from './JobDetail';
import { DeadLink, JobCard, JobNotice, JobPageShell } from './JobShell';

type Phase = 'restoring' | 'challenge' | 'ready';

/**
 * A supplier's job, opened from an emailed vetter link.
 *
 * The access token lives in memory only, so on a fresh load the session is
 * restored from the refresh cookie — but only when this tab already worked
 * through THIS link. A cookie is per app, not per link: opening a second
 * link must not show the job the cookie was minted for.
 */
export function JobPage() {
	const [searchParams] = useSearchParams();
	const { token, setAccessToken, setUser } = useAuth();

	const urlToken = searchParams.get('token');
	const storedToken = getJobLinkToken();
	const linkToken = urlToken ?? storedToken;
	const sameLink = !!storedToken && storedToken === linkToken;

	const [phase, setPhase] = useState<Phase>(() =>
		token && sameLink ? 'ready' : sameLink ? 'restoring' : 'challenge'
	);
	const [expired, setExpired] = useState(false);
	const [deadMessage, setDeadMessage] = useState<string | null>(null);

	const [refreshToken] = useRefreshTokenMutation();
	const restoreStarted = useRef(false);

	useEffect(() => {
		if (phase !== 'restoring' || restoreStarted.current) return;
		restoreStarted.current = true;
		refreshToken()
			.then(({ data }) => {
				const res = data?.refreshToken;
				if (res?.token && res.user.role === AuthRoleEnum.Vetter) {
					setUser(formatLoginUser(res.user as AuthenticationUser));
					setAccessToken(res.token);
					setPhase('ready');
				} else {
					setExpired(true);
					setPhase('challenge');
				}
			})
			.catch(() => {
				setExpired(true);
				setPhase('challenge');
			});
	}, [phase, refreshToken, setAccessToken, setUser]);

	if (deadMessage !== null) return <DeadLink message={deadMessage} />;

	if (!linkToken) {
		return (
			<JobCard>
				<JobNotice
					title="This link is incomplete"
					detail="Open the link from your email again — it carries a code this page needs."
				/>
			</JobCard>
		);
	}

	if (phase === 'restoring') return <Loading />;

	if (phase === 'challenge') {
		return (
			<JobCard>
				<JobAuthChallenge
					linkToken={linkToken}
					expired={expired}
					onAuthenticated={() => setPhase('ready')}
					onDeadLink={setDeadMessage}
				/>
			</JobCard>
		);
	}

	return (
		<JobView
			onDead={(message) => {
				clearJobLinkToken();
				setDeadMessage(message);
			}}
			onNotLinkSession={() => {
				// A vetter session that did not come through a link — not ours to show.
				setExpired(false);
				setPhase('challenge');
			}}
		/>
	);
}

function JobView({
	onDead,
	onNotLinkSession,
}: {
	onDead: (message: string) => void;
	onNotLinkSession: () => void;
}) {
	const { data, loading, error, refetch } = useGetVetterJobQuery({ fetchPolicy: 'no-cache' });
	const job = data?.getVetterJob;

	const errorMessage = error?.graphQLErrors[0]?.message ?? error?.message ?? null;
	const dead = !!errorMessage && classifyLinkFailure(errorMessage) === 'terminal';
	const notLinkSession = !loading && !error && data && !job;

	useEffect(() => {
		if (dead && errorMessage) onDead(errorMessage);
	}, [dead, errorMessage, onDead]);

	useEffect(() => {
		if (notLinkSession) onNotLinkSession();
	}, [notLinkSession, onNotLinkSession]);

	if (loading && !job) return <Loading />;

	if (!job) {
		if (dead || notLinkSession) return <Loading />;
		return (
			<JobCard>
				<JobNotice
					title="Your price request could not be loaded"
					detail={errorMessage ?? 'Try reloading the page.'}
				/>
			</JobCard>
		);
	}

	return (
		<JobPageShell>
			<JobDetail job={job} header={<LinkJobHeader job={job} />} onChanged={refetch} />
		</JobPageShell>
	);
}

export function Loading() {
	return (
		<div className="flex h-full items-center justify-center">
			<Loader2 size={18} className="animate-spin text-text-4" />
		</div>
	);
}
